import { Badge } from '@/components/ui';
import { TablePagination } from '@/components/ui/TablePagination';
import { Eye, Edit, Trash2, MoreVertical } from 'lucide-react';
import { useState } from 'react';
import type { EquipmentListItem } from '../types/equipmentList.types';

const STATUS_MAP: Record<string, { label: string; className: string }> = {
  active:      { label: 'Hoạt động',    className: 'bg-emerald-50 text-emerald-600 border-emerald-100' },
  inactive:    { label: 'Không hoạt động', className: 'bg-slate-100 text-slate-500 border-slate-200' },
  available:   { label: 'Sẵn sàng',     className: 'bg-blue-50 text-blue-600 border-blue-100' },
  in_use:      { label: 'Đang sử dụng', className: 'bg-emerald-50 text-emerald-600 border-emerald-100' },
  assigned:    { label: 'Đã điều phối', className: 'bg-emerald-50 text-emerald-600 border-emerald-100' },
  maintenance: { label: 'Bảo trì',      className: 'bg-orange-50 text-orange-600 border-orange-100' },
  repairing:   { label: 'Đang sửa',     className: 'bg-amber-50 text-amber-600 border-amber-100' },
  damaged:     { label: 'Hư hỏng',      className: 'bg-rose-50 text-rose-600 border-rose-100' },
  broken:      { label: 'Hỏng',         className: 'bg-rose-50 text-rose-600 border-rose-100' },
  retired:     { label: 'Thanh lý',     className: 'bg-slate-100 text-slate-500 border-slate-200' },
};

interface EquipmentMobileCardsProps {
  items: EquipmentListItem[];
  total: number;
  page: number;
  pageSize: number;
  isLoading?: boolean;
  onView: (id: string) => void;
  onEdit: (id: string) => void;
  onDelete: (item: EquipmentListItem) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  canEdit?: boolean;
  canDelete?: boolean;
}

function CardSkeleton() {
  return (
    <div className="bg-white rounded-md border border-slate-100 p-4 animate-pulse">
      <div className="flex gap-3">
        <div className="w-12 h-12 rounded-md bg-slate-100 shrink-0" />
        <div className="flex-1 space-y-2">
          <div className="h-3 bg-slate-100 rounded w-3/4" />
          <div className="h-2.5 bg-slate-100 rounded w-1/2" />
          <div className="h-5 bg-slate-100 rounded w-20 mt-2" />
        </div>
      </div>
    </div>
  );
}

export function EquipmentMobileCards({
  items, total, page, pageSize, isLoading,
  onView, onEdit, onDelete, onPageChange, onPageSizeChange,
  canEdit, canDelete,
}: EquipmentMobileCardsProps) {
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);

  return (
    <div className="md:hidden space-y-3">
      {isLoading
        ? Array.from({ length: 5 }).map((_, i) => <CardSkeleton key={i} />)
        : items.length === 0
        ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3 text-slate-400">
            <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center">
              <i className="ri-tools-line text-xl" />
            </div>
            <p className="sm:text-sm text-xs">Không có thiết bị nào</p>
          </div>
        )
        : items.map((item) => {
          const normalizedStatus = (item.status_code || '').trim().toLowerCase();
          const status = STATUS_MAP[normalizedStatus] ?? { label: item.status_code, className: 'bg-slate-50 text-slate-500' };
          return (
            <div
              key={item.id}
              onClick={() => onView(item.id)}
              className="bg-white rounded-md border border-slate-100 p-4 cursor-pointer hover:border-slate-200 transition-colors"
            >
              <div className="flex gap-3">
                <img 
                  src={item.photo_url || '/assets/other/img/equipment.png'} 
                  alt="" 
                  className="w-12 h-12 rounded-md object-cover border border-slate-200 shrink-0" 
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className="sm:text-sm text-xs text-slate-800 line-clamp-2">{item.name}</p>
                    <button
                      onClick={(e) => { e.stopPropagation(); setMenuOpenId(menuOpenId === item.id ? null : item.id); }}
                      className="p-1 rounded text-slate-400 hover:text-slate-600 shrink-0"
                    >
                      <MoreVertical className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5">{item.code || '-'} • {item.group_name || 'Chưa phân nhóm'}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge variant="custom" className={`rounded border sm:text-xs text-[10px] px-2 py-0.5 ${status.className}`}>
                      {status.label}
                    </Badge>
                  </div>
                </div>
              </div>
              {menuOpenId === item.id && (
                <div className="mt-3 pt-3 border-t border-slate-50 flex gap-2" onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => onView(item.id)} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md bg-slate-50 sm:text-sm text-xs text-slate-600">
                    <Eye className="w-3.5 h-3.5" /> Xem
                  </button>
                  {canEdit && (
                    <button onClick={() => onEdit(item.id)} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md bg-slate-50 sm:text-sm text-xs text-slate-600">
                      <Edit className="w-3.5 h-3.5" /> Sửa
                    </button>
                  )}
                  {canDelete && (
                    <button onClick={() => onDelete(item)} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md bg-rose-50 sm:text-sm text-xs text-rose-600">
                      <Trash2 className="w-3.5 h-3.5" /> Xóa
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      <TablePagination
        total={total}
        page={page}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </div>
  );
}
